import {
  usePlayer,
  usePlayers,
  useRound,
  useStage,
  useGame,
} from "@empirica/core/player/classic/react";
import { Loading } from "@empirica/core/player/react";
import React from "react";
import { Choice } from "./stages/Choice.jsx";
import { Discussion } from "./stages/Discussion.jsx";
import { FinalVote } from "./stages/FinalVote.jsx";
import { PostPeerBelief } from "./stages/PostPeerBelief.jsx";
import { PostPeerReflection } from "./stages/PostPeerReflection.jsx";
import { Result } from "./stages/Result.jsx";
// AssistantChat removed - AI discussion now happens in pre-lobby
import { AIDiscuss } from "./stages/AIDiscuss.jsx";
import { AISummary } from "./stages/AISummary.jsx";
import { LobbyWaiting } from "./components/LobbyWaiting.jsx";
import { isBaseRateStaticSyntheticGroupCondition } from "./utils/conditionUtils.js";

export function Stage() {
  const player = usePlayer();
  const players = usePlayers();
  const round = useRound();
  const stage = useStage();
  const game = useGame();
  const prevStageIdRef = React.useRef(stage?.id);

  // Resolve treatment type for client-side guards
  const treatment = game?.get("treatment") || {};
  const treatmentType = treatment.treatmentType ||
    ((treatment.name || "").toLowerCase().includes("synthetic") ? "Synthetic Peers" :
     (treatment.name || "").toLowerCase().includes("no-ai") ? "No-AI Control" :
     (treatment.name || "").toLowerCase().includes("nogroup") ? "nogroup" :
     (treatment.name || "").toLowerCase().includes("control") ? "Control" : undefined);
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const playerCount = treatment.playerCount ||
    treatment?.factors?.playerCount ||
    game?.get("playerCount");

  // Auto-skip unwanted stages if they appear in stale sequences
  React.useEffect(() => {
    if (!stage || !player) return;
    if (player.stage.get("submit")) return;
    const name = stage.get("name");
    const preLobbyCompleted = player.get("preLobbyCompleted");

    if (name === "choice" && treatmentType === "Control") {
      console.info("[CLIENT] Auto-skip choice for Control; submitting stage", { stage: name });
      player.stage.set("submit", true);
    }
    if (name === "choice" && treatmentType === "No-AI Control" && preLobbyCompleted) {
      console.info("[CLIENT] Auto-skip choice for No-AI Control (pre-lobby completed); submitting stage", { stage: name });
      player.stage.set("submit", true);
    }
    if (
      name === "discussion" &&
      (treatmentType === "nogroup" ||
        (treatmentType === "Control" &&
          playerCount === 1 &&
          !isBaseRateStaticSyntheticGroup))
    ) {
      console.info("[CLIENT] Auto-skip discussion for nogroup/Control (1p); submitting stage", { stage: name });
      player.stage.set("submit", true);
    }
    // Assistant stage removed from all sequences - AI discussion now happens in pre-lobby
    // aiSummary should only appear for AI-Discuss and Synthetic Peers conditions
    if (name === "aiSummary" && treatmentType !== "AI-Discuss" && treatmentType !== "Synthetic Peers") {
      console.info("[CLIENT] Auto-skip aiSummary for non-AI-Discuss/Synthetic-Peers conditions; submitting stage", { stage: name, treatmentType });
      player.stage.set("submit", true);
    }
    // Auto-submit the minimal "exit" stage for nogroup conditions
    if (name === "exit") {
      console.info("[CLIENT] Auto-skip exit stage for nogroup; submitting stage", { stage: name });
      player.stage.set("submit", true);
    }
  }, [stage?.id, treatmentType, player, playerCount, isBaseRateStaticSyntheticGroup]);

  // Handle individual progression for result stage - now handled directly in Result component

  const stageJustChanged = prevStageIdRef.current !== stage?.id;
  if (stageJustChanged) {
    console.info("[CLIENT] Stage changed", { prev: prevStageIdRef.current, next: stage?.id, name: stage?.get("name") });
  }

  // Retract a lingering submit flag ONLY on aiSummary. That is the one stage the
  // server-side advance watcher never covers: onStageStart returns before the
  // 1000 ms poll is installed (server/src/callbacks.js, the aiSummary branch), so
  // if Empirica's own submit hook loses a race there the only other backstop is
  // the stage duration itself (180 s for AI-Discuss, 1200 s for Synthetic Peers)
  // and this retraction is what lets a second click recover instead. It is kept
  // here unchanged rather than fixed, because no groupfirst arm has an aiSummary
  // stage and the real repair is to give that stage a watcher server-side.
  //
  // It used to run on every stage except result and finalVote, and that was the
  // cause of the "I had to click Continue twice" reports. `player.stage.get` is
  // optimistic (the local value is written before the mutation is sent), so in a
  // playerCount: 1 treatment `players.every(...)` is already true on the render
  // that follows the participant's own submit, and this block retracted that
  // submit a few milliseconds later. The server then read `false` and never ended
  // the stage, while the 1000 ms poll in callbacks.js could not see a
  // millisecond-wide window. Because the retraction also flipped `playerSubmitted`
  // back to false below, the same page re-rendered with every local field intact,
  // so the click looked like it had simply done nothing.
  //
  // A genuinely stale flag is still cleared on stage change by the effect below,
  // which re-runs because Game.jsx remounts <Stage> on stage.id / stageBeginTick.
  const currentStageName = stage?.get("name");
  if (currentStageName === "aiSummary") {
    const allSubmitted = players.every((p) => p.stage.get("submit"));
    if (allSubmitted && player.stage.get("submit")) {
      console.info("[CLIENT] All players submitted; clearing local submit to continue", { stageId: stage?.id });
      player.stage.set("submit", false);
    }
  }

  // Mark enteredAt when a stage is first rendered for this player
  React.useEffect(() => {
    if (!player || !stage) return;
    if (!player.stage.get("enteredAt")) {
      console.info("[CLIENT] Stage entered", { name: stage.get("name"), stageId: stage.id, playerId: player.id, players: players.length, treatmentType });
      player.stage.set("enteredAt", Date.now());
    }
    // Clear stale submit flag on stage change to avoid stuck UI
    // But preserve submit status for result stage (individual progression)
    const stageNameLocal = stage.get("name");
    if (player.stage.get("submit") && stageNameLocal !== "result") {
      player.stage.set("submit", false);
    }
    // update previous stage id after effects
    prevStageIdRef.current = stage?.id;
  }, [player?.id, stage?.id]);

  // Check if player is ready to exit (completed result stage individually)
  const readyToExit = player?.get("readyToExit");
  if (readyToExit) {
    console.info("[CLIENT] Player is ready to exit, returning null to let Empirica handle exit flow");
    return null;
  }

  // Check if player hasn't completed pre-lobby prediction yet
  const preLobbyCompleted = player?.get("preLobbyCompleted");
  if (!preLobbyCompleted) {
    console.info("[CLIENT] Waiting for player to complete pre-lobby prediction");
    return (
      <div className="text-center space-y-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Preparing Your Session
          </h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-lg text-blue-800 mb-4">
              Please complete the initial prediction step to continue.
            </p>
            <div className="text-sm text-blue-700">
              <p>
                {isBaseRateStaticSyntheticGroup
                  ? "This prepares the group review for your session."
                  : "This ensures everyone is ready before we start matching you with other participants."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if we're in the lobby (pre-lobby completed but no game started yet)
  // Only show lobby if game hasn't started yet - avoid race conditions when multiple games start simultaneously
  const gameStarted = game?.get("startedAt");
  if (!stage || !round) {
    if (!gameStarted) {
      if (isBaseRateStaticSyntheticGroup) {
        console.info("[CLIENT] Static synthetic group: suppressing lobby wait screen before stage hooks are ready");
        return <Loading />;
      }
      console.info("[CLIENT] In lobby - pre-lobby completed, waiting for game to start");
      return <LobbyWaiting onTimeout={() => {
        // Force a page reload to trigger exit survey
        window.location.reload();
      }} />;
    } else {
      // Game has started but hooks haven't updated yet - show loading instead of lobby
      console.info("[CLIENT] Game started but stage/round hooks not ready yet, showing loading");
      return <Loading />;
    }
  }

  // Check if we should show pre-game waiting (after instructions, before first stage)
  const currentStageIndex = round?.get("stageIndex") || 0;
  const isFirstStage = currentStageIndex === 0;

  if (
    gameStarted &&
    isFirstStage &&
    !player.stage.get("submit") &&
    !isBaseRateStaticSyntheticGroup
  ) {
    console.info("[CLIENT] Showing pre-game waiting - after instructions, before first stage");
    return (
      <WaitingForOthersInline
        stageName="Getting ready for the experiment..."
        isPreGame={true}
      />
    );
  }

  // After hooks are declared, decide whether to render the waiting screen
  // Skip waiting screen for result stage (individual progression)
  const stageName = stage?.get("name");
  const playerSubmitted = player.stage.get("submit");

  if (playerSubmitted && !stageJustChanged && stageName !== "result" && stageName !== "finalVote") {
    if (players.length === 1) {
      return <Loading />;
    }
    return <WaitingForOthersInline stageName={stageName} />;
  }

  switch (stageName) {
    case "choice":
      return <Choice />;
    case "discussion":
      return <Discussion />;
    case "aiDiscuss":
      return <AIDiscuss />;
    case "aiSummary":
      return <AISummary />;
    // The post-peer stages are only added to the sequence for post-peer
    // conditions, so they need no auto-skip entry above.
    case "postPeerBelief":
      return <PostPeerBelief />;
    case "postPeerReflection":
      return <PostPeerReflection />;
    case "finalVote":
      return <FinalVote />;
    case "result":
      return <Result />;
    case "exit":
      // Minimal exit stage for nogroup — auto-submitted by useEffect above
      return <Loading />;
    default:
      return <Loading />;
  }
}

function WaitingForOthersInline({ stageName, isPreGame = false }) {
  const [waitTime, setWaitTime] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setWaitTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStageDescription = (stageName) => {
    switch (stageName) {
      case "choice":
        return "making their initial predictions";
      case "discussion":
        return "sharing their thoughts in the discussion";
      case "aiDiscuss":
        return "reviewing the AI-generated discussion";
      case "aiSummary":
        return "reviewing the AI discussion summary";
      case "assistant":
        return "interacting with the AI assistant";
      case "finalVote":
        return "casting their final votes";
      default:
        return "completing their tasks";
    }
  };

  const getEncouragingMessage = () => {
    const messages = [
      "Thanks for your patience!",
      "Your input is valuable to our research.",
      "This helps us understand group decision-making.",
      "Great job staying engaged!",
      "Your participation makes a difference."
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Different content for pre-game vs in-game waiting
  const title = isPreGame ? "Preparing the Experiment" : "Waiting for Other Participants";
  const explanation = isPreGame
    ? "We're setting up your personalized experiment environment. This ensures everyone starts at the same time for a fair experience."
    : `Other participants are currently ${getStageDescription(stageName)}. Once everyone has completed this stage, the game will continue automatically.`;

  const stageInfo = isPreGame
    ? "Getting ready for the experiment..."
    : stageName;

  return (
    <div className="text-center space-y-6 max-w-2xl mx-auto p-8 min-h-[60vh] flex flex-col items-center justify-center">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {title}
          </h2>

          <div className="text-sm text-gray-600 mb-4">
            <span className="font-medium">Wait time: </span>
            <span className="font-mono text-lg text-blue-600">{formatTime(waitTime)}</span>
          </div>
        </div>

        <div className="space-y-4 text-left">
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h3 className="font-semibold text-gray-800 mb-2">What's happening?</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {explanation}
            </p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h3 className="font-semibold text-gray-800 mb-2">Status</h3>
            <p className="text-gray-700 text-sm">
              <strong className="text-blue-600">{stageInfo}</strong>
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-green-800 text-sm font-medium">
              {getEncouragingMessage()}
            </p>
          </div>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          <p>Please keep this window open. The experiment will begin automatically.</p>
        </div>
      </div>
    </div>
  );
}
